/**
 * Daily challenge helper.
 *
 * The /daily landing page links to every game with ?daily=YYYY-MM-DD
 * baked in. Each game's main-*.ts calls getDailySeed() during start;
 * if non-null, the run uses that seed deterministically and skips
 * the /api/runs/<game> POST (daily runs are unranked).
 *
 * Same date → same seed across all games, so today's Pulse, Spire,
 * and Drift all share an axis the community can compare scores on.
 */

const DAILY_PROFILE_KEY = "aether.daily";

export function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function dailyParam(): string | null {
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get("daily");
  } catch {
    return null;
  }
}

/** Hash an arbitrary string into a 31-bit non-negative seed. */
export function seedFromString(s: string): number {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return ((h ^ (h >>> 16)) >>> 0) & 0x7fffffff;
}

/** If ?daily=YYYY-MM-DD or ?seed=N is present, return that seed; else null.
 *  Daily takes priority when both are set. Used by every game's startup
 *  to honor shared-seed URLs (daily challenge + replay-share). */
export function getDailySeed(): number | null {
  // Daily challenge first.
  const d = dailyParam();
  if (d) return seedFromString(d);
  // Then explicit ?seed=N (replay-share).
  try {
    const raw = new URL(window.location.href).searchParams.get("seed");
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0 || n > 0x7fffffff) return null;
    return n;
  } catch { return null; }
}

/** True iff the current page was opened as a daily challenge. */
export function isDailyRun(): boolean {
  return dailyParam() !== null;
}

interface DailyProgress {
  date: string;
  completed: string[];     // game ids
  scores: Record<string, number>;
}

function getDailyProgress(): DailyProgress {
  try {
    const raw = localStorage.getItem(DAILY_PROFILE_KEY);
    if (!raw) return { date: todayKey(), completed: [], scores: {} };
    const p = JSON.parse(raw) as DailyProgress;
    // Reset progress when the date rolls over.
    if (p.date !== todayKey()) return { date: todayKey(), completed: [], scores: {} };
    return p;
  } catch {
    return { date: todayKey(), completed: [], scores: {} };
  }
}

/** Record a completed daily run. Returns the updated progress. */
export function markDailyComplete(game: string, score: number): DailyProgress {
  const p = getDailyProgress();
  if (!p.completed.includes(game)) p.completed.push(game);
  if (!p.scores[game] || score > p.scores[game]) p.scores[game] = score;
  try { localStorage.setItem(DAILY_PROFILE_KEY, JSON.stringify(p)); }
  catch { /* private mode */ }
  return p;
}

export function getDailyProgressPublic(): DailyProgress { return getDailyProgress(); }
